import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Card,
  CardMedia,
  IconButton,
  Switch,
  FormControlLabel,
  Collapse,
  Divider
} from '@mui/material';
import { CloudUpload, Delete } from '@mui/icons-material';
import {
  dialogFormPaperSx,
  dialogFormScrollContentSx,
  dialogFormTitleSx,
  dialogFormActionsSx,
  uploadDropzoneSx,
  uploadDropzoneCloudIconSx
} from '../utils/dialogFormLayout';

const SetForm = ({ open, onClose, onSubmit, set = null, isEdit = false, characters = [] }) => {
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [description, setDescription] = useState('');
  const [instruction, setInstruction] = useState('');
  const [characterId, setCharacterId] = useState('');
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [coverImage, setCoverImage] = useState('');
  const [coverFile, setCoverFile] = useState(null);
  const [enableReward, setEnableReward] = useState(false);
  const [requiredRelationshipPoints, setRequiredRelationshipPoints] = useState('');
  const [rewardTitle, setRewardTitle] = useState('');
  const [rewardDescription, setRewardDescription] = useState('');
  const fileInputRef = useRef();

  // Update form fields when set prop changes
  useEffect(() => {
    if (set) {
      console.log('Loading set data for editing:', set);
      setName(set.name || '');
      setPrice(set.price || '');
      setDescription(set.description || '');
      setInstruction(set.instruction || '');
      setCharacterId(set.characterId?._id || set.characterId || '');
      setTags(set.tags || []);
      setCoverImage(set.coverImage || '');
      setCoverFile(null);
      const hasReward = set.requiredRelationshipPoints !== null && set.requiredRelationshipPoints !== undefined;
      setEnableReward(hasReward);
      setRequiredRelationshipPoints(hasReward ? String(set.requiredRelationshipPoints || '') : '');
      setRewardTitle(set.rewardTitle || '');
      setRewardDescription(set.rewardDescription || '');
    } else {
      setName('');
      setPrice('');
      setDescription('');
      setInstruction('');
      setCharacterId('');
      setTags([]);
      setCoverImage('');
      setCoverFile(null);
      setEnableReward(false);
      setRequiredRelationshipPoints('');
      setRewardTitle('');
      setRewardDescription('');
    }
  }, [set]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (name.trim() && price !== '' && characterId) {
      console.log('Submitting form with data:', {
        name: name.trim(),
        price: Number(price),
        description: description.trim(),
        instruction: instruction.trim(),
        characterId,
        tags,
        coverFile: coverFile ? {
          name: coverFile.name,
          size: coverFile.size,
          type: coverFile.type
        } : null,
        existingCoverImage: coverImage && !coverFile ? coverImage : null
      });
      
      onSubmit({ 
        name: name.trim(),
        price: Number(price),
        description: description.trim(),
        instruction: instruction.trim(),
        characterId,
        tags,
        coverFile,
        existingCoverImage: coverImage && !coverFile ? coverImage : null,
        requiredRelationshipPoints: enableReward && requiredRelationshipPoints ? Number(requiredRelationshipPoints) : null,
        rewardTitle: enableReward ? rewardTitle.trim() : '',
        rewardDescription: enableReward ? rewardDescription.trim() : ''
      });
      setName('');
      setPrice('');
      setDescription('');
      setInstruction('');
      setCharacterId('');
      setTags([]);
      setCoverImage('');
      setCoverFile(null);
      setEnableReward(false);
      setRequiredRelationshipPoints('');
      setRewardTitle('');
      setRewardDescription('');
      onClose();
    }
  };

  const handleClose = () => {
    setName('');
    setPrice('');
    setDescription('');
    setCharacterId('');
    setTags([]);
    setEnableReward(false);
    setRequiredRelationshipPoints('');
    setRewardTitle('');
    setRewardDescription('');
    onClose();
  };

  const handleTagAdd = (event) => {
    if (event.key === 'Enter' && tagInput.trim()) {
      event.preventDefault();
      if (!tags.includes(tagInput.trim())) {
        setTags([...tags, tagInput.trim()]);
      }
      setTagInput('');
    }
  };

  const handleTagDelete = (tagToDelete) => {
    setTags(tags.filter(tag => tag !== tagToDelete));
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    console.log('File selected:', file);
    if (file) {
      setCoverFile(file);
      setCoverImage(URL.createObjectURL(file));
      console.log('File set in state:', file.name, file.size, file.type);
    }
  };

  const handleRemoveCover = () => {
    setCoverFile(null);
    setCoverImage('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const openFileDialog = () => {
    fileInputRef.current.click();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: dialogFormPaperSx }}
    >
      <DialogTitle sx={dialogFormTitleSx}>
        {isEdit ? 'Edit Set' : 'Create New Set'}
      </DialogTitle>
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: '1 1 auto',
          minHeight: 0,
          overflow: 'hidden'
        }}
      >
        <DialogContent sx={(theme) => dialogFormScrollContentSx(theme)}>
          <TextField
            autoFocus
            margin="dense"
            label="Set Name"
            type="text"
            fullWidth
            variant="outlined"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            sx={{ mb: 2 }}
          />
          
          <TextField
            margin="dense"
            label="Price"
            type="number"
            fullWidth
            variant="outlined"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            required
            inputProps={{ min: 0 }}
            sx={{ mb: 2 }}
          />
          
          <FormControl fullWidth margin="normal" required sx={{ mb: 2 }}>
            <InputLabel id="character-select-label">Character</InputLabel>
            <Select
              labelId="character-select-label"
              id="character-select"
              value={characterId}
              label="Character"
              onChange={(e) => setCharacterId(e.target.value)}
            >
              {characters.map((character) => (
                <MenuItem key={character._id} value={character._id}>
                  {character.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Cover Image Upload */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Cover Image
            </Typography>
            {coverImage ? (
              <Card sx={{ mb: 2, maxWidth: 300 }}>
                <CardMedia
                  component="img"
                  height="200"
                  image={coverImage}
                  alt="Cover"
                  sx={{ objectFit: 'cover' }}
                />
                <Box sx={{ p: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    {coverFile ? coverFile.name : 'Cover image'}
                  </Typography>
                  <IconButton size="small" onClick={handleRemoveCover} color="error">
                    <Delete />
                  </IconButton>
                </Box>
              </Card>
            ) : (
              <Box sx={(theme) => uploadDropzoneSx(theme)} onClick={openFileDialog}>
                <CloudUpload sx={uploadDropzoneCloudIconSx} />
                <Typography variant="body1" color="text.secondary">
                  Click to upload cover image
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Supports: JPG, PNG, GIF
                </Typography>
              </Box>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
          </Box>
          
          <TextField
            margin="dense"
            label="Description"
            multiline
            rows={3}
            fullWidth
            variant="outlined"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Enter set description..."
            sx={{ mb: 2 }}
          />
          
          <TextField
            margin="dense"
            label="Instruction"
            multiline
            rows={4}
            fullWidth
            variant="outlined"
            value={instruction}
            onChange={(e) => setInstruction(e.target.value)}
            placeholder="Enter instruction text for this set..."
            sx={{ mb: 2 }}
          />
          
          <TextField
            margin="dense"
            label="Add Tags (press Enter to add)"
            fullWidth
            variant="outlined"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyPress={handleTagAdd}
            placeholder="Type tag and press Enter..."
            sx={{ mb: 2 }}
          />
          
          {tags.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Tags:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {tags.map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    onDelete={() => handleTagDelete(tag)}
                    size="small"
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* Reward Section */}
          <Divider sx={{ my: 3 }} />
          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={enableReward}
                  onChange={(e) => setEnableReward(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Typography variant="subtitle1" fontWeight="medium">
                  Enable Relationship Reward
                </Typography>
              }
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, ml: 4.5 }}>
              Allow this album to be unlocked as a reward when reaching certain relationship points
            </Typography>
          </Box>

          <Collapse in={enableReward}>
            <Box sx={{ mt: 2, pl: 4.5, pr: 2 }}>
              <TextField
                margin="dense"
                label="Required Relationship Points"
                type="number"
                fullWidth
                variant="outlined"
                value={requiredRelationshipPoints}
                onChange={(e) => setRequiredRelationshipPoints(e.target.value)}
                inputProps={{ min: 0 }}
                helperText="Minimum relationship points needed to unlock this album"
                sx={{ mb: 2 }}
              />
              
              <TextField
                margin="dense"
                label="Reward Title"
                type="text"
                fullWidth
                variant="outlined"
                value={rewardTitle}
                onChange={(e) => setRewardTitle(e.target.value)}
                placeholder="e.g., Special Gift from Character"
                sx={{ mb: 2 }}
              />
              
              <TextField
                margin="dense"
                label="Reward Description"
                multiline
                rows={3}
                fullWidth
                variant="outlined"
                value={rewardDescription}
                onChange={(e) => setRewardDescription(e.target.value)}
                placeholder="Describe the reward that will be shown to the user..."
                sx={{ mb: 2 }}
              />
            </Box>
          </Collapse>
        </DialogContent>
        <DialogActions sx={dialogFormActionsSx}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button type="submit" variant="contained">
            {isEdit ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default SetForm; 